import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { useDrawer } from '../context/DrawerContext';
import { useToast } from '../context/ToastContext';
import '../styles/Home.css';
import logo from '../assets/logo/light.png';
import {
  LayoutDashboard, Key, Webhook, ScrollText, Store, Plug,
  BookOpen, ChevronRight, Menu, X, Trash2, UtensilsCrossed,
} from 'lucide-react';
import { PiSealCheckFill } from 'react-icons/pi';
import UserMenu from '../components/UserMenu';


const API_BASE = process.env.REACT_APP_API_BASE;

// ── API helper ───────────────────────────────────
async function apiFetch(endpoint, body, token) {
  const base = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
  const res = await fetch(`${base}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', AUTH: token },
    body: JSON.stringify(body),
  });
  let data;
  const text = await res.text();
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Server error (${res.status})${text ? ': ' + text.slice(0, 120) : ''}`);
  }
  if (!data.success) throw new Error(data.response || 'Request failed');
  return data;
}

function formatDate(ts) {
  return new Date((ts || 0) * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Mock event data (logs / overview preview) ────


const PAYLOAD_NEW = `{
  "event": "order.new",
  "data": {
    "merchant_token": "downtown",
    "order": {
      "id":      489734,
      "status":  "pending",
      "type":    "delivery",
      "when":    "now",
      "payment": { "method": "cash", "amount": 600 },
      "customer": {
        "name":  "John Doe",
        "phone": "+5978123456"
      },
      "delivery": {
        "address":  "Henck Arronstraat 12",
        "coords":   { "lat": 5.8332, "lng": -55.1668 },
        "comments": "Ring the bell",
        "fee": 80
      },
      "items": [
        {
          "title":       "Chicken Burger",
          "key":         "chicken-burger",
          "variant":     "Large",
          "variant_key": "large",
          "qty":         2,
          "total":       140,
          "modifiers": [
            { "group": "Extras", "group_key": "extras", "choices": [
                { "title": "Extra Sauce", "key": "extra-sauce" }
            ]}
          ],
          "instructions": ""
        }
      ],
      "totals": {
        "curr": "SRD",
        "order": 420, "delivery": 80, "total": 521
      }
    }
  }
}`;

const PAYLOAD_PUSH = `POST https://ride-eats.com/api2/sync/order/push
Authorization: re_live_a3f8c2...

{
  "id":     489734,
  "status": "accepted",
  "data":   { "time": 20 }
}

// 200 OK
{
  "success": true,
  "data": {
    "order_id":        489734,
    "previous_status": "pending",
    "new_status":      "accepted"
  }
}`;

// ── Helpers ─────────────────────────────────────
function StatusPill({ status }) {
  return <span className={`status-pill sp-${status}`}>{status}</span>;
}

function timeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

function EventTable({ events, onRowClick }) {
  if (!events || events.length === 0) {
    return <div className="empty-state" style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>No events yet. Events appear here once your webhooks start receiving orders.</div>;
  }
  return (
    <table className="event-table list" style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th>Event</th>
          <th>Order ID</th>
          <th>Order status</th>
          <th>Merchant</th>
          <th>Status</th>
          <th>Type</th>
          <th>Time</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {events.map(e => (
          <tr key={e.id} className={onRowClick ? 'event-row-clickable' : ''} onClick={() => onRowClick && onRowClick(e)}>
            <td><span className={`event-type ${e.event === 'order.new' ? 'new' : 'update'}`}>{e.event}</span></td>
            <td><span className="event-order-id">#{e.order_id}</span></td>
            <td>{e.order_status ? <StatusPill status={e.order_status} /> : <span style={{ color: 'var(--muted)' }}>...</span>}</td>
            <td><span className="merchant-token">{e.merchant_token}</span></td>
            <td><StatusPill status={e.status} /></td>
            <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{e.type}</td>
            <td><span className="event-time">{timeAgo(e.timestamp)}</span></td>
            <td className="event-row-arrow">{onRowClick && <span className="event-chevron">›</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Inner content rendered inside the DrawerContext shell.
 * Open it anywhere with:
 *   const { openDrawer, closeDrawer } = useDrawer();
 *   openDrawer(<LogOrderDetail log={e} token={token} />);
 */
function LogOrderDetail({ log, token, onRetried }) {
  const { closeDrawer } = useDrawer();
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [logStatus,   setLogStatus]   = useState(log.status);
  const [httpStatus,  setHttpStatus]  = useState(log.http_status);
  const [retrying,    setRetrying]    = useState(false);
  const [retryError,  setRetryError]  = useState(null);

  useEffect(() => {
    setLoading(true); setError(null); setData(null);
    apiFetch('dev/log/order', { log_id: log.id }, token)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [log.id, token]);

  async function doRetry() {
    setRetrying(true); setRetryError(null);
    try {
      const res = await apiFetch('dev/log/retry', { log_id: log.id }, token);
      setLogStatus(res.data.status);
      setHttpStatus(res.data.http_status);
      if (onRetried) onRetried(log.id, res.data.status, res.data.http_status);
    } catch (err) {
      setRetryError(err.message);
    } finally {
      setRetrying(false);
    }
  }

  function fmt(ts) {
    if (!ts) return '..';
    return new Date(ts * 1000).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  function currency(n) { return 'SRD ' + parseFloat(n || 0).toFixed(2); }

  return (
    <>
      <div className="drawer-header">
        <div>
          <div className="drawer-title">Order #{log.order_id}</div>
          <div className="drawer-subtitle">
            <span className={`event-type ${log.event === 'order.new' ? 'new' : 'update'}`}>{log.event}</span>
            <span>{timeAgo(log.timestamp)}</span>
          </div>
        </div>
        <button className="drawer-close" onClick={closeDrawer}>✕</button>
      </div>

      <div className="drawer-body">
        {loading && (
          <div className="drawer-loading">
            <div className="loading-row" style={{ marginBottom: 10 }} />
            <div className="loading-row" style={{ width: '70%', marginBottom: 10 }} />
            <div className="loading-row" style={{ width: '80%' }} />
          </div>
        )}
        {error && <div className="drawer-error">{error}</div>}
        {data && (() => {
          const { order } = data;
          // contact is a nested object: { contact: {name,phone}, address: {...}, payment: {...} }
          const contactObj  = Array.isArray(order.contact) ? {} : (order.contact  || {});
          const deliveryObj = Array.isArray(order.delivery) ? {} : (order.delivery || {});
          const contactInfo = contactObj.contact || {};
          const addressInfo = contactObj.address  || deliveryObj;
          const merchant    = order.merchant || {};
          const totals      = order.totals   || {};
          const items       = order.items    || [];

          const customerName  = order.customer?.name  || contactInfo.name  || '...';
          const customerPhone = order.customer?.phone || contactInfo.phone || '...';
          const addressLine   = addressInfo.address
            ? [addressInfo.address, addressInfo.address_num].filter(Boolean).join(' ')
            : null;

          return (
            <>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">{log.event === 'order.update' ? 'Changed to' : 'Status'}</span>
                <StatusPill status={data.log.order_status || order.status} />
              </div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Placed</span>
                <span className="drawer-val">{fmt(order.timestamp)}</span>
              </div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Payment</span>
                <span className="drawer-val" style={{ textTransform: 'uppercase', fontWeight: 700 }}>{order.payment_method}</span>
              </div>
              {order.schedule === 'scheduled' && (
                <div className="drawer-row drawer-row--inline">
                  <span className="drawer-label">Scheduled</span>
                  <span className="drawer-val">{fmt(order.schedule_time)}</span>
                </div>
              )}

              <div className="drawer-divider" />

              <div className="drawer-section-title">Merchant</div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Name</span>
                <span className="drawer-val">{merchant.name}</span>
              </div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Token</span>
                <span className="merchant-token">{log.merchant_token}</span>
              </div>

              <div className="drawer-divider" />

              <div className="drawer-section-title">Customer</div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Name</span>
                <span className="drawer-val">{customerName}</span>
              </div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Phone</span>
                <span className="drawer-val">{customerPhone}</span>
              </div>
              {addressLine && (
                <div className="drawer-row drawer-row--inline">
                  <span className="drawer-label">Address</span>
                  <span className="drawer-val">{addressLine}</span>
                </div>
              )}

              <div className="drawer-divider" />

              <div className="drawer-section-title">Items</div>
              <div className="drawer-items">
                {items.map((item, i) => {
                  // Support both v2 shape {dish, variant, qty, totals} and flat {title, qty, total}
                  const title    = item.dish?.title || item.title || item.name || '?';
                  const variant  = item.variant?.title || (typeof item.variant === 'string' ? item.variant : null);
                  const qty      = item.qty ?? 1;
                  const amount   = item.totals ?? item.total ?? item.dish?.price?.amount ?? 0;
                  return (
                    <div key={i} className="drawer-item">
                      <div className="drawer-item-name">
                        {title}
                        {variant && <span className="drawer-item-variant"> · {variant}</span>}
                        {qty > 1 && <span className="drawer-item-qty"> ×{qty}</span>}
                      </div>
                      <div className="drawer-item-price">{currency(amount)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="drawer-divider" />

              <div className="drawer-section-title">Totals</div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Subtotal</span>
                <span className="drawer-val">{currency(totals.order)}</span>
              </div>
              {(order.extra_fees || []).map((fee, i) => {
                const label = fee.type === 'checkout_fees_delivery'
                  ? 'Delivery fee'
                  : fee.type === 'checkout_fees_service_fee'
                  ? 'Service fee'
                  : fee.type?.includes('coupon')
                  ? 'Discount'
                  : fee.type ?? 'Fee';
                const isDiscount = fee.type?.includes('coupon');
                const amt = typeof fee.amount === 'string' && fee.amount.includes('%')
                  ? fee.amount
                  : currency(fee.amount);
                return (
                  <div key={i} className="drawer-row drawer-row--inline">
                    <span className="drawer-label">{label}</span>
                    <span className="drawer-val" style={isDiscount ? { color: 'var(--accent2)' } : undefined}>
                      {isDiscount ? '-' : ''}{amt}
                    </span>
                  </div>
                );
              })}
              {deliveryObj.distance > 0 && (
                <div className="drawer-row drawer-row--inline">
                  <span className="drawer-label">Distance</span>
                  <span className="drawer-val" style={{ color: 'var(--muted)' }}>{deliveryObj.distance} km</span>
                </div>
              )}
              <div className="drawer-row drawer-row--inline drawer-row--total">
                <span className="drawer-label">Total</span>
                <span className="drawer-val">{currency(totals.total)}</span>
              </div>

              <div className="drawer-divider" />

              <div className="drawer-section-title">Webhook delivery</div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">Result</span>
                <StatusPill status={logStatus} />
              </div>
              <div className="drawer-row drawer-row--inline">
                <span className="drawer-label">HTTP status</span>
                <span className="drawer-val">{httpStatus || '...'}</span>
              </div>

              {data.log.payload && (() => {
                let pretty = data.log.payload;
                try { pretty = JSON.stringify(JSON.parse(data.log.payload), null, 2); } catch {}
                return (
                  <>
                    <div className="drawer-divider" />
                    <div className="drawer-section-title">Payload sent</div>
                    <Snippet lang="JSON" code={pretty} />
                  </>
                );
              })()}
            </>
          );
        })()}
      </div>

      {logStatus === 'failed' && (
        <div className="drawer-footer">
          {retryError && (
            <div style={{ fontSize: 12, color: '#f87171', marginBottom: 8 }}>{retryError}</div>
          )}
          <button
            className="btn-sm accent drawer-footer-btn"
            onClick={doRetry}
            disabled={retrying}
          >
            {retrying ? <><span className="btn-spinner" /> Retrying…</> : 'Retry delivery'}
          </button>
        </div>
      )}
    </>
  );
}

function Snippet({ lang, code }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <div className="snippet-block">
      <div className="snippet-header">
        <span className="snippet-lang">{lang}</span>
        <button className="snippet-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre>{code}</pre>
    </div>
  );
}

// ── Form Modal (reusable overlay with form content) ─
function FormModal({ title, onClose, onSubmit, submitLabel = 'Save', children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        {title && (
          <div className="modal-header">
            <h2 className="modal-title">{title}</h2>
          </div>
        )}
        <div className={`modal-body${!title ? ' modal-body--no-header' : ''}`}>
          <div className="modal-content">
            <div className="modal-form-row">{children}</div>
          </div>
        </div>
        <div className="modal-footer">
          <div className="modal-actions">
            <button className="modal-btn modal-btn--confirm" onClick={onSubmit}>
              {submitLabel}
            </button>
            <button className="modal-btn modal-btn--cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Loading / Error states ────────────────────────
function LoadingState({ rows = 3 }) {
  return (
    <div className="dash-content">
      <div className="loading-rows">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="loading-row" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="dash-content">
      <div className="error-state">
        <span>{message || 'Failed to load data.'}</span>
        {onRetry && <button className="btn-sm" style={{ marginLeft: 12 }} onClick={onRetry}>Retry</button>}
      </div>
    </div>
  );
}

// ── Pages ────────────────────────────────────────
function OverviewPage({ setPage, keys, merchants, recentLogs, stats, token }) {
  const { openDrawer } = useDrawer();
  const liveCount = keys.filter(k => k.env === 'live').length;
  const testCount = keys.filter(k => k.env === 'test').length;
  const webhookActive = keys.length > 0;
  return (
    <div className="dash-content">

      <div className="stats-row">
        <div className="stat-item">
          <span className="stat-label">Merchants</span>
          <span className="stat-value">{merchants.length}</span>
          <span className="stat-sub">{merchants.filter(m => m.token).map(m => m.token).join(' · ') || '...'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">API Keys</span>
          <span className="stat-value">{keys.length}</span>
          <span className="stat-sub">{liveCount} live · {testCount} test</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Events today</span>
          <span className="stat-value">{stats ? stats.today : '—'}</span>
          <span className="stat-sub">{stats ? `${stats.today_new} new · ${stats.today_updates} updates` : '...'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Webhook</span>
          <span className={`stat-value ${webhookActive ? 'ov-stat-active' : ''}`}>{webhookActive ? 'Active' : 'Inactive'}</span>
          <span className="stat-sub">{stats ? stats.total : '...'} total events logged</span>
        </div>
      </div>

      <div className="qa-grid">
        <button className="qa-card" onClick={() => setPage('api-keys')}>
          <Key size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">API Keys</div>
            <div className="qa-card-sub">{keys.length} active, {merchants.filter(m => m.token).length} configured</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('webhooks')}>
          <Webhook size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Webhooks</div>
            <div className="qa-card-sub">Per-key callback URLs</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('merchants')}>
          <Store size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Merchants</div>
            <div className="qa-card-sub">{merchants.length} locations linked to account</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
        <button className="qa-card" onClick={() => setPage('logs')}>
          <ScrollText size={16} className="qa-card-icon" />
          <div className="qa-card-body">
            <div className="qa-card-title">Event Logs</div>
            <div className="qa-card-sub">{stats ? stats.total : '...'} total events logged</div>
          </div>
          <ChevronRight size={15} className="qa-card-arrow" />
        </button>
      </div>

      <div className="event-feed">
        <div className="section-title">Recent events</div>
        <EventTable events={recentLogs.slice(0, 6)} onRowClick={e => openDrawer(
          <LogOrderDetail log={e} token={token} onRetried={() => {}} />
        )} />
      </div>

    </div>
  );
}

function ApiKeysPage({ keys, setKeys, merchants, token, onSetupWebhook }) {
  const { openModal } = useModal();
  const [revealed,       setRevealed]       = useState({});
  const [copied,         setCopied]         = useState({});
  const [toggling,       setToggling]       = useState({});
  const [showNewKey,     setShowNewKey]     = useState(false);
  const [newName,        setNewName]        = useState('');
  const [newEnv,         setNewEnv]         = useState('live');
  const [newMerchants,   setNewMerchants]   = useState([]);
  const [creating,       setCreating]       = useState(false);
  const [createError,    setCreateError]    = useState('');
  const [editMerchKey,   setEditMerchKey]   = useState(null); // key object being edited
  const [editMerchSel,   setEditMerchSel]   = useState([]);
  const [savingMerch,    setSavingMerch]    = useState(false);
  const [saveMerchErr,   setSaveMerchErr]   = useState('');

  function toggleReveal(id) { setRevealed(r => ({ ...r, [id]: !r[id] })); }

  function copyKey(id, val) {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(c => ({ ...c, [id]: true }));
      setTimeout(() => setCopied(c => ({ ...c, [id]: false })), 2000);
    });
  }

  async function doToggle(k) {
    const next = !k.enabled;
    setToggling(t => ({ ...t, [k.id]: true }));
    try {
      await apiFetch('dev/key/toggle', { key_id: k.id, enabled: next ? 1 : 0 }, token);
      setKeys(ks => ks.map(x => x.id === k.id ? { ...x, enabled: next } : x));
    } catch (err) {
      openModal({ title: 'Error', text: err.message, cancel: { label: 'OK' } });
    } finally {
      setToggling(t => ({ ...t, [k.id]: false }));
    }
  }

  function handleToggleClick(k) {
    if (k.enabled) {
      openModal({
        title: 'Disable API key',
        text: `"${k.name}" will stop accepting requests immediately. You can re-enable it at any time.`,
        confirm: { label: 'Disable key', danger: true, onClick: () => doToggle(k) },
        cancel:  { label: 'Keep enabled' },
      });
    } else {
      doToggle(k);
    }
  }

  async function doDelete(key_id) {
    try {
      await apiFetch('dev/key/delete', { key_id }, token);
      setKeys(ks => ks.filter(k => k.id !== key_id));
    } catch (err) {
      openModal({ title: 'Error', text: err.message, cancel: { label: 'OK' } });
    }
  }

  function confirmDelete(k) {
    openModal({
      title: 'Delete API key',
      text: `"${k.name}" will be permanently removed. Any integration using this key will stop working immediately.`,
      confirm: { label: 'Delete key', danger: true, onClick: () => doDelete(k.id) },
      cancel:  { label: 'Keep it' },
    });
  }

  function openNewKey() {
    setNewName(''); setNewEnv('live'); setNewMerchants([]); setCreateError(''); setShowNewKey(true);
  }

  function toggleNewMerchant(t) {
    setNewMerchants(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function createKey() {
    if (!newName.trim()) { setCreateError('Key name is required.'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const res = await apiFetch('dev/key/create', {
        name: newName.trim(),
        env:  newEnv,
        merchants: newMerchants,
      }, token);
      setKeys(ks => [res.data.key, ...ks]);
      setShowNewKey(false);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  function openEditMerchants(k) {
    setEditMerchKey(k);
    setEditMerchSel(k.merchants ? [...k.merchants] : []);
    setSaveMerchErr('');
  }

  function toggleEditMerch(t) {
    setEditMerchSel(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function saveEditMerchants() {
    if (!editMerchKey) return;
    setSavingMerch(true);
    setSaveMerchErr('');
    try {
      const res = await apiFetch('dev/key/merchants/update', {
        key_id:    editMerchKey.id,
        merchants: editMerchSel,
      }, token);
      setKeys(ks => ks.map(k => k.id === editMerchKey.id ? { ...k, merchants: res.data.merchants } : k));
      setEditMerchKey(null);
    } catch (err) {
      setSaveMerchErr(err.message);
    } finally {
      setSavingMerch(false);
    }
  }

  const configuredMerchants = (merchants || []).filter(m => m.token);

  return (
    <div className="dash-content">
      {showNewKey && (
        <FormModal
          title="New API key"
          onClose={() => setShowNewKey(false)}
          onSubmit={createKey}
          submitLabel={creating ? 'Creating…' : 'Create key'}
        >
          {createError && <div className="form-error">{createError}</div>}
          <div>
            <label className="field-label">Key name</label>
            <input
              className="field-input"
              placeholder="e.g. KFC Kwatta POS"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Environment</label>
            <select className="dash-select" value={newEnv} onChange={e => setNewEnv(e.target.value)}>
              <option value="live">Live</option>
              <option value="test">Test</option>
            </select>
          </div>
          {configuredMerchants.length > 0 && (
            <div>
              <label className="field-label">Linked merchants</label>
              <div className="merchant-check-list">
                {configuredMerchants.map(m => (
                  <label key={m.id} className="merchant-check-item">
                    <input
                      type="checkbox"
                      checked={newMerchants.includes(m.token)}
                      onChange={() => toggleNewMerchant(m.token)}
                    />
                    <span className="merchant-token">{m.token}</span>
                    <span className="merchant-check-name">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {configuredMerchants.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0' }}>
              No merchant tokens configured yet. Set up tokens in the Merchants tab first.
            </p>
          )}
        </FormModal>
      )}

      {editMerchKey !== null && (
        <FormModal
          title={`Merchants | ${editMerchKey.name}`}
          onClose={() => setEditMerchKey(null)}
          onSubmit={saveEditMerchants}
          submitLabel={savingMerch ? 'Saving…' : 'Save'}
        >
          {saveMerchErr && <div className="form-error">{saveMerchErr}</div>}
          {configuredMerchants.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              No merchant tokens configured yet. Go to the <strong style={{ color: 'var(--text)' }}>Merchants</strong> tab and set up tokens first.
            </p>
          ) : (
            <div>
              <label className="field-label">Linked merchants</label>
              <div className="merchant-check-list">
                {configuredMerchants.map(m => (
                  <label key={m.id} className="merchant-check-item">
                    <input
                      type="checkbox"
                      checked={editMerchSel.includes(m.token)}
                      onChange={() => toggleEditMerch(m.token)}
                    />
                    <span className="merchant-token">{m.token}</span>
                    <span className="merchant-check-name">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </FormModal>
      )}

      <div className="section-header-row">
        <div className="section-title">API Keys</div>
        <button className="btn-sm accent" onClick={openNewKey}>+ New key</button>
      </div>

      {keys.length === 0 && (
        <div className="empty-state">
          <h3>No API keys yet</h3>
          <p>Create your first key to start integrating with the Ride Eats platform.</p>
        </div>
      )}

      <div className="key-list">
        {keys.map(k => (
          <div className={`key-card${k.enabled ? '' : ' key-card--disabled'}`} key={k.id}>
            <div className="key-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="key-card-name">{k.name}</span>
                <span className={`key-env-badge ${k.env === 'live' ? 'live' : 'test'}`}>{k.env}</span>
                {!k.enabled && <span className="key-disabled-badge">Disabled</span>}
              </div>
              <div className="key-card-actions">
                <button
                  className={`key-toggle-btn ${k.enabled ? 'enabled' : 'off'}`}
                  onClick={() => handleToggleClick(k)}
                  disabled={!!toggling[k.id]}
                >
                  {toggling[k.id] ? '…' : k.enabled ? 'Enabled' : 'Enable'}
                </button>
                <button className="btn-sm" onClick={() => openEditMerchants(k)}>Merchants</button>
                <button className="btn-sm" onClick={() => onSetupWebhook(k.id)}>Webhooks</button>
                <button className="key-delete-btn" onClick={() => confirmDelete(k)} title="Delete key"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="key-value-block">
              <span className={`key-value-text${revealed[k.id] ? '' : ' masked'}`}>
                {revealed[k.id] ? k.value : k.value.slice(0, 8) + '•'.repeat(24)}
              </span>
              <div className="key-value-actions">
                <button className="key-copy-btn" onClick={() => toggleReveal(k.id)}>
                  {revealed[k.id] ? 'Hide' : 'Reveal'}
                </button>
                <button
                  className={`key-copy-btn${copied[k.id] ? ' copied' : ''}`}
                  onClick={() => copyKey(k.id, k.value)}
                >
                  {copied[k.id] ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="key-meta-row">
              <div className="key-meta-item">
                <span className="key-meta-label">Created</span>
                <span className="key-meta-value">{formatDate(k.created)}</span>
              </div>
              <div className="key-meta-item">
                <span className="key-meta-label">Merchants</span>
                <span className="key-meta-value">{k.merchants.length > 0 ? k.merchants.join(' · ') : '...'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 40 }}>
        <div className="section-title">How to authenticate</div>
        <Snippet lang="HTTP" code={`POST https://ride-eats.com/api2/sync/order/info\nContent-Type: application/json\nAPI: re_live_a3f8c2...\n\n{ "id": 489734 }`} />
      </div>
    </div>
  );
}

function WebhooksPage({ keys, webhookConfigs, setWebhookConfigs, token, selectedKeyId: selectedKeyIdProp, setSelectedKeyId: setSelectedKeyIdProp }) {
  const selectedKeyId = selectedKeyIdProp ?? keys[0]?.id ?? null;
  const [saved,  setSaved]  = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const activeId = (keys.find(k => k.id === selectedKeyId) ? selectedKeyId : keys[0]?.id) ?? null;
  const cfg = webhookConfigs[activeId] || { urlNew: '', urlUpdate: '' };
  const anyConfigured = cfg.urlNew || cfg.urlUpdate;

  function setUrl(field, value) {
    if (activeId === null) return;
    setSaveErr('');
    setWebhookConfigs(prev => ({
      ...prev,
      [activeId]: { ...cfg, [field]: value },
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (activeId === null) return;
    setSaving(true);
    setSaveErr('');
    try {
      await apiFetch('dev/webhook/save', {
        key_id:     activeId,
        url_new:    cfg.urlNew,
        url_update: cfg.urlUpdate,
      }, token);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveErr(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dash-content">
      <div className="webhook-section">
        <div className="section-header-row" >
          <div className="section-title">Callback URLs</div>
          {keys.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Create an API key first</span>
          )}
          {keys.length === 1 && (
            <span className={`key-env-badge ${keys[0].env === 'live' ? 'live' : 'test'}`}>{keys[0].name}</span>
          )}
          {keys.length > 1 && (
            <select
              className="dash-select"
              value={activeId ?? ''}
              onChange={e => setSelectedKeyIdProp(Number(e.target.value))}
            >
              {keys.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.env})</option>
              ))}
            </select>
          )}
        </div>
        {keys.length > 0 && (
          <div className="webhook-status-row">
            <div className={`wh-status-dot ${anyConfigured ? 'active' : 'inactive'}`} />
            <span className="wh-status-label">{anyConfigured ? 'Endpoints configured, receiving events' : 'No endpoints set for this key'}</span>
          </div>
        )}
        <form className="webhook-form" onSubmit={save}>
          {keys.length === 0 ? (
            <div className="webhook-empty-state">
              <Key size={28} style={{ color: 'var(--red)', marginBottom: 10 }} />
              <div className="webhook-empty-title">No API keys yet</div>
              <p className="webhook-empty-body">
                Webhook callback URLs are tied to an API key. Create an API key first, then come back here to configure your endpoints.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="field-label">New order callback URL <span style={{ color: 'var(--accent2)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(order.new)</span></label>
                <input
                  className="field-input full"
                  type="url"
                  value={cfg.urlNew}
                  onChange={e => setUrl('urlNew', e.target.value)}
                  placeholder="https://your-system.com/webhooks/new-order"
                />
              </div>
              <div>
                <label className="field-label">Order update callback URL <span style={{ color: 'var(--yellow)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(order.update)</span></label>
                <input
                  className="field-input full"
                  type="url"
                  value={cfg.urlUpdate}
                  onChange={e => setUrl('urlUpdate', e.target.value)}
                  placeholder="https://your-system.com/webhooks/order-update"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="submit" className="btn-sm accent" disabled={saving}>
                  {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save endpoints'}
                </button>
                {saveErr && <span style={{ fontSize: 12, color: '#f87171' }}>{saveErr}</span>}
              </div>
            </>
          )}
        </form>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div className="section-title">Events sent to your endpoint</div>
        <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr><th>Event type</th><th>When fired</th><th>Payload</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="event-type new">order.new</span></td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Customer places an order</td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Full order object (items, customer, totals, delivery)</td>
            </tr>
            <tr>
              <td><span className="event-type update">order.update</span></td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>Any status change (by you, biker, or platform)</td>
              <td style={{ fontSize: '13px', color: 'var(--muted)' }}>order_id, previous_status, new_status, extras</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="two-col">
        <div>
          <div className="section-title">order.new payload</div>
          <Snippet lang="JSON" code={PAYLOAD_NEW} />
        </div>
        <div>
          <div className="section-title">order.update payload</div>
          <Snippet lang="JSON" code={`{
  "event": "order.update",
  "data": {
    "merchant_token":  "downtown",
    "order_id":        489734,
    "previous_status": "pending",
    "new_status":      "accepted",
    "status_updated":  1745413000,
    "extras": {
      "time": 20
    }
  }
}`} />
        </div>
      </div>
    </div>
  );
}

function LogsPage({ logs, setLogs, live, setLive, token, keys, keyFilter, setKeyFilter, logsReady, initLogs }) {
  const PER_PAGE = 25;
  const { openDrawer } = useDrawer();

  const [filter,       setFilter]       = useState('all');
  const [page,         setPage]         = useState(1);
  const [logsLoading,  setLogsLoading]  = useState(false);
  const topRef = useRef(null);
  const didInitRef = useRef(false);

  // Lazy-load full logs on first visit
  useEffect(() => {
    if (logsReady || didInitRef.current) return;
    didInitRef.current = true;
    setLogsLoading(true);
    apiFetch('dev/logs', {}, token)
      .then(d => initLogs(d.data.logs || []))
      .catch(() => initLogs([]))
      .finally(() => setLogsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function goToPage(fn) {
    setPage(fn);
    topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  // Global logs scoped to the selected key (client-side, instant)
  const keyLogs = keyFilter === 0 ? logs : logs.filter(e => e.key_id === keyFilter);

  // Merchant tab list ... seeded from in-memory logs immediately, then augmented by API
  const fromLogs = [...new Set(keyLogs.map(e => e.merchant_token).filter(Boolean))];
  const [extraMerchants, setExtraMerchants] = useState([]);
  const merchantTokens = [...new Set([...fromLogs, ...extraMerchants])];

  // Per-merchant log cache keyed by "keyFilter:merchantToken"
  const [merchantLogsCache, setMerchantLogsCache] = useState({});
  const [merchantLoading,   setMerchantLoading]   = useState(false);

  // Fetch merchant list whenever the key selection changes
  useEffect(() => {
    const body = keyFilter > 0 ? { key_id: keyFilter } : {};
    apiFetch('dev/log/merchants', body, token).then(res => {
      setExtraMerchants(res.data.merchants || []);
    }).catch(() => {});
  }, [token, keyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // When key changes, reset event filter + page and clear stale merchant cache
  function changeKey(kid) {
    setKeyFilter(kid);
    setFilter('all');
    setPage(1);
    setExtraMerchants([]);
    setMerchantLogsCache({});
  }

  // Jump to page 1 when new events arrive (logs grows)
  const prevLenRef = useRef(logs.length);
  useEffect(() => {
    if (logs.length > prevLenRef.current) setPage(1);
    prevLenRef.current = logs.length;
  }, [logs]);

  // Sync live-polled entries into the active merchant cache so the table
  // stays current without re-fetching when you're on a merchant tab.
  useEffect(() => {
    if (!isMerchantFilter) return;
    const cKey = `${keyFilter}:${filter}`;
    const cached = merchantLogsCache[cKey];
    if (!cached) return; // tab not yet loaded, nothing to patch
    const cachedIds = new Set(cached.map(e => e.id));
    const fresh = keyLogs.filter(
      e => e.merchant_token === filter && !cachedIds.has(e.id)
    );
    if (fresh.length === 0) return;
    setMerchantLogsCache(prev => ({ ...prev, [cKey]: [...fresh, ...(prev[cKey] || [])] }));
  }, [logs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whether filter is a merchant-scoped tab
  const isMerchantFilter = filter !== 'all' && filter !== 'order.new' && filter !== 'order.update';

  // Fetch merchant logs on tab click (only if not already cached for this key)
  function selectMerchant(merchantToken) {
    setFilter(merchantToken);
    setPage(1);
    const cacheKey = `${keyFilter}:${merchantToken}`;
    if (merchantLogsCache[cacheKey]) return;
    setMerchantLoading(true);
    const body = { merchant_token: merchantToken, ...(keyFilter > 0 ? { key_id: keyFilter } : {}) };
    apiFetch('dev/logs', body, token).then(res => {
      const ml = res.data.logs || [];
      setMerchantLogsCache(prev => ({ ...prev, [cacheKey]: ml }));
    }).catch(() => {
      setMerchantLogsCache(prev => ({ ...prev, [cacheKey]: [] }));
    }).finally(() => setMerchantLoading(false));
  }

  // Source list: merchant-scoped or key-filtered global
  const cacheKey   = `${keyFilter}:${filter}`;
  const sourceList = isMerchantFilter ? (merchantLogsCache[cacheKey] || []) : keyLogs;

  const filtered = filter === 'all'
    ? sourceList
    : filter === 'order.new' || filter === 'order.update'
    ? sourceList.filter(e => e.event === filter)
    : sourceList; // already merchant-scoped
  const totalPages  = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const visible     = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  return (
    <div className="dash-content">
      <div ref={topRef} />
      <div className="logs-header">
        <div className="logs-filter-row">
          {['all', 'order.new', 'order.update'].map(f => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => { setFilter(f); setPage(1); }}
            >
              {f === 'all' ? 'All events' : f}
            </button>
          ))}
          {merchantTokens.length > 0 && (
            <>
              <span className="filter-divider" />
              {merchantTokens.map(t => (
                <button
                  key={t}
                  className={`filter-btn ${filter === t ? 'active' : ''}`}
                  onClick={() => selectMerchant(t)}
                >
                  {t}
                  {merchantLoading && filter === t && <span className="filter-btn-spinner" />}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="logs-controls">
          {keys.length > 1 && (
            <select
              className="dash-select"
              value={keyFilter}
              onChange={e => changeKey(Number(e.target.value))}
            >
              <option value={0}>All keys</option>
              {keys.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.env})</option>
              ))}
            </select>
          )}
          <button
            className={`logs-live-btn${live ? ' live' : ' paused'}`}
            onClick={() => setLive(l => !l)}
          >
            <span className={`live-dot${live ? ' pulsing' : ''}`} />
            {live ? 'Live' : 'Paused'}
          </button>
          <button
            className="logs-clear-btn"
            onClick={() => { setLogs([]); setPage(1); }}
          >
            Clear
          </button>
        </div>
      </div>

      {logsLoading && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>Loading logs…</div>
      )}

      {!logsLoading && <EventTable events={visible} onRowClick={e => openDrawer(
        <LogOrderDetail
          log={e}
          token={token}
          onRetried={(id, status, http) =>
            setLogs(prev => prev.map(l => l.id === id ? { ...l, status, http_status: http } : l))
          }
        />
      )} />}

      {!logsLoading && filtered.length > PER_PAGE && (
        <div className="logs-pagination">
          <button
            className="page-btn"
            onClick={() => goToPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="page-info">{currentPage} / {totalPages}</span>
          <button
            className="page-btn"
            onClick={() => goToPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

    </div>
  );
}

function EndpointsPage() {
  return (
    <div className="dash-content">
      <div style={{ marginBottom: 40 }}>
        <div className="section-title">POST /order/create</div>
        <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginBottom: 16 }}>
          Create a Ride Eats order from your system using merchant tokens and custom menu keys. Ride Eats resolves the menu data and inserts the platform order.
        </p>
        <div className="two-col">
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Request</div>
            <Snippet lang="JSON" code={`{
  "external_order_id": "pos-100928",
  "merchant_token": "downtown",
  "type": "delivery",
  "when": "now",
  "payment": { "method": "cash" },
  "customer": {
    "name": "John Doe",
    "phone": "+5978123456"
  },
  "delivery": {
    "address": "Henck Arronstraat 12",
    "coords": { "lat": 5.8332, "lng": -55.1668 },
    "comments": "Ring the bell"
  },
  "items": [
    {
      "key": "chicken-burger",
      "variant_key": "large",
      "qty": 2,
      "modifiers": [
        { "group_key": "extras", "choices": ["extra-sauce"] }
      ],
      "instructions": ""
    }
  ]
}`} />
          </div>
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Response</div>
            <Snippet lang="JSON" code={`{
  "success": true,
  "response": "Order created.",
  "data": {
    "id": 489800,
    "external_order_id": "pos-100928",
    "merchant_token": "downtown",
    "order": {
      "id": 489800,
      "status": "pending",
      "type": "delivery",
      "items": [ ... ]
    }
  }
}`} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div className="section-title">POST /order/info</div>
        <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginBottom: 16 }}>
          Fetch full details for one or multiple orders. Orders must belong to a merchant linked to your API key.
        </p>
        <div className="two-col">
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Single request</div>
            <Snippet lang="JSON" code={`{ "id": 489734 }`} />
            <div className="section-title" style={{ marginTop: 20, marginBottom: 8 }}>Batch request (max 50)</div>
            <Snippet lang="JSON" code={`{ "ids": [489734, 489733, 489732] }`} />
          </div>
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Response</div>
            <Snippet lang="JSON" code={`{
  "success":  true,
  "response": "Order info loaded.",
  "data": {
    "merchant_token": "downtown",
    "order": {
      "id":             489734,
      "placed_on":      1745412125,
      "status":         "accepted",
      "status_updated": 1745412300,
      "type":           "delivery",
      "when":           "now",
      "payment":  { "method": "cash", "amount": 600 },
      "customer": { "name": "John Doe", "phone": "+5978..." },
      "delivery": {
        "address":  "Henck Arronstraat 12",
        "coords":   { "lat": 5.8332, "lng": -55.1668 },
        "comments": "Ring the bell",
        "fee": 80
      },
      "biker": null,
      "items": [ { "title": "Chicken Burger", "key": "chicken-burger", "variant": "Large", "variant_key": "large", ... } ],
      "totals": {
        "curr": "SRD",
        "order": 420, "delivery": 80, "total": 521
      }
    }
  }
}`} />
          </div>
        </div>
      </div>

      <div>
        <div className="section-title">POST /order/push</div>
        <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginBottom: 16 }}>
          Advance an order through its status lifecycle. Transitions are strictly enforced; out-of-order pushes are rejected.
        </p>
        <div className="two-col">
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Allowed values</div>
            <table className="event-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th>Send</th><th>Use when</th></tr></thead>
              <tbody>
                <tr><td><StatusPill status="accepted"/></td><td style={{fontSize:'13px',color:'var(--muted)'}}>You confirm the order</td></tr>
                <tr><td><span className="status-pill sp-ready">ready</span></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Food is ready (delivery or pickup)</td></tr>
                <tr><td><span className="status-pill sp-ontheway">ontheway</span></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Biker picked up (delivery only)</td></tr>
                <tr><td><StatusPill status="completed"/></td><td style={{fontSize:'13px',color:'var(--muted)'}}>Order delivered or picked up</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>Request & response</div>
            <Snippet lang="JSON" code={PAYLOAD_PUSH} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Drawer content showing all API keys linked to a merchant.
 * Admin can see keys from all accounts; regular users see only their own.
 */
function MerchantDetail({ merchant, token, isAdmin }) {
  const { closeDrawer } = useDrawer();
  const { openModal }   = useModal();
  const [keys,    setKeys]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    apiFetch('dev/merchant/keys', { place_id: merchant.id }, token)
      .then(res => { setKeys(res.data.keys); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [merchant.id, token]);

  function confirmUnlink(key) {
    openModal({
      title: 'Unlink API key',
      text: `Remove "${key.name}" from ${merchant.name}? The key will still exist but will no longer receive events for this merchant.`,
      confirm: { label: 'Unlink', danger: true, onClick: () => doUnlink(key.id) },
      cancel:  { label: 'Cancel' },
    });
  }

  async function doUnlink(key_id) {
    try {
      await apiFetch('dev/merchant/key/unlink', { key_id, place_id: merchant.id }, token);
      setKeys(prev => prev.filter(k => k.id !== key_id));
    } catch (err) {
      openModal({ title: 'Error', text: err.message, cancel: { label: 'OK' } });
    }
  }

  return (
    <>
      <div className="drawer-header">
        <div>
          <div className="drawer-title">{merchant.name}</div>
          <div className="drawer-subtitle">
            {merchant.token
              ? <span className="merchant-token">{merchant.token}</span>
              : <span style={{ color: 'var(--muted)', fontSize: 12 }}>No token configured</span>
            }
          </div>
        </div>
        <button className="drawer-close" onClick={closeDrawer}>✕</button>
      </div>

      <div className="drawer-body">
        <div className="drawer-section-title">Linked API Keys</div>

        {loading && (
          <div className="drawer-loading">
            <div className="loading-row" style={{ marginBottom: 10 }} />
            <div className="loading-row" style={{ width: '70%', marginBottom: 10 }} />
            <div className="loading-row" style={{ width: '80%' }} />
          </div>
        )}
        {error && <div className="drawer-error">{error}</div>}
        {keys && keys.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>
            No API keys are linked to this merchant.
          </div>
        )}
        {keys && keys.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {keys.map(key => (
              <div key={key.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8,
                border: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: key.enabled ? 'var(--text)' : 'var(--muted)' }}>
                      {key.name}
                    </span>
                    <span className={`key-env-badge ${key.env === 'live' ? 'live' : 'test'}`}>{key.env}</span>
                    {!key.enabled && <span className="key-disabled-badge">Disabled</span>}
                  </div>
                  {isAdmin && key.account_name && (
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{key.account_name}</span>
                  )}
                </div>
                <button
                  className="key-delete-btn"
                  onClick={() => confirmUnlink(key)}
                  title="Unlink from merchant"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function MerchantsPage({ merchants, setMerchants, token, isAdmin }) {
  const { openDrawer }  = useDrawer();
  const [search,     setSearch]     = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');
  const [togglingId, setTogglingId] = useState(null);

  function openTokenModal(m) {
    setTokenInput(m.token || '');
    setSaveError('');
    setEditTarget(m.id);
  }

  async function saveToken() {
    const t = tokenInput.trim().toLowerCase();
    if (!t) { setSaveError('Token is required.'); return; }
    if (!/^[a-z0-9][a-z0-9_-]{1,63}$/.test(t)) {
      setSaveError('2–64 chars: lowercase letters, numbers, hyphens, underscores.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await apiFetch('dev/merchant/token/set', { place_id: editTarget, token: t }, token);
      setMerchants(ms => ms.map(m => m.id === editTarget ? { ...m, token: t } : m));
      setEditTarget(null);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleDeveloper(m) {
    setTogglingId(m.id);
    try {
      const next = !m.developer_enabled;
      await apiFetch('dev/merchant/developer/toggle', { place_id: m.id, enabled: next ? 1 : 0 }, token);
      setMerchants(ms => ms.map(x => x.id === m.id ? { ...x, developer_enabled: next } : x));
    } catch { /* silently fail */ }
    setTogglingId(null);
  }

  const editMerchant = merchants.find(m => m.id === editTarget);

  return (
    <div className="dash-content">
      {editTarget !== null && (
        <FormModal
          title={editMerchant?.token ? 'Edit merchant token' : 'Set up merchant token'}
          onClose={() => setEditTarget(null)}
          onSubmit={saveToken}
          submitLabel={saving ? 'Saving…' : 'Save token'}
        >
          {saveError && <div className="form-error">{saveError}</div>}
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 10px' }}>
            <strong style={{ color: 'var(--text)' }}>{editMerchant?.name}</strong> | this token identifies the merchant in every webhook payload.
          </p>
          <div>
            <label className="field-label">Token</label>
            <input
              className="field-input"
              placeholder="e.g. kfc-kwatta"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value.toLowerCase())}
              autoFocus
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
              Lowercase letters, numbers, hyphens, underscores. 2–64 characters.
            </div>
          </div>
        </FormModal>
      )}

      <div className="section-header-row">
        <div className="section-title">Merchants</div>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Synced from your Ride Eats account</span>
      </div>

      <div className="merchants-search-bar">
        <input
          className="field-input merchants-search full"
          placeholder="Search by name or token…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 520 }}
        />
      </div>

      <table className="event-table list merchants-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th>Merchant</th>
            <th>Token</th>
            <th>Status</th>
            {isAdmin && <th>Developer</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {merchants.filter(m => {
            if (!search.trim()) return true;
            const q = search.trim().toLowerCase();
            return m.name.toLowerCase().includes(q) || (m.token || '').toLowerCase().includes(q);
          }).map(m => (
            <tr
              key={m.id}
              className="event-row-clickable"
              style={isAdmin && !m.developer_enabled ? { opacity: 0.55 } : undefined}
              onClick={() => openDrawer(<MerchantDetail merchant={m} token={token} isAdmin={isAdmin} />)}
            >
              <td style={{ fontSize: 13.5, color: 'var(--text)', fontWeight: 500 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {m.name}
                  {m.verified && <PiSealCheckFill size={16} style={{ color: '#0482ee', flexShrink: 0 }} />}
                </span>
              </td>
              <td>
                {m.token
                  ? <span className="merchant-token">{m.token}</span>
                  : <span className="setup-badge">Not configured</span>
                }
              </td>
              <td>
                {m.token
                  ? <span className="status-pill sp-active">Active</span>
                  : <span className="status-pill sp-inactive">Inactive</span>
                }
              </td>
              {isAdmin && (
                <td onClick={e => e.stopPropagation()}>
                  <button
                    className={`btn-sm${m.developer_enabled ? ' btn-sm-danger' : ''}`}
                    onClick={() => toggleDeveloper(m)}
                    disabled={togglingId === m.id}
                    style={{ minWidth: 80 }}
                  >
                    {togglingId === m.id
                      ? '…'
                      : m.developer_enabled ? 'Disable dev' : 'Enable dev'
                    }
                  </button>
                </td>
              )}
              <td onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {(!isAdmin || m.developer_enabled) && (
                    <button className="btn-sm" onClick={() => openTokenModal(m)}>
                      {m.token ? 'Edit token' : 'Set up'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ marginTop: 24, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
        Merchant tokens appear as <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent2)' }}>merchant_token</code> in every webhook payload. Each merchant without a token will not receive webhooks and cannot be linked to new API keys.
      </p>
    </div>
  );
}

// ── Menu Items Page ──────────────────────────────────────────────
function MenuItemsPage({ merchants, token, keys }) {
  const { addToast } = useToast();

  // Selectors
  const [selKeyId,   setSelKeyId]   = useState(() => (keys || [])[0]?.id || 0);

  // Merchants linked to the selected key
  const selectedKey       = (keys || []).find(k => k.id === selKeyId);
  const linkedTokens      = selectedKey?.merchants || [];
  const linkedMerchants   = (merchants || []).filter(m => m.token && linkedTokens.includes(m.token));

  const [selPlaceId, setSelPlaceId] = useState(() => linkedMerchants[0]?.id || 0);

  // When the key changes, reset place to first linked merchant
  const prevKeyIdRef = useRef(selKeyId);
  useEffect(() => {
    if (prevKeyIdRef.current === selKeyId) return;
    prevKeyIdRef.current = selKeyId;
    const newKey      = (keys || []).find(k => k.id === selKeyId);
    const newTokens   = newKey?.merchants || [];
    const newMerchants = (merchants || []).filter(m => m.token && newTokens.includes(m.token));
    setSelPlaceId(newMerchants[0]?.id || 0);
  }, [selKeyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remote data
  const [data,    setData]    = useState(null);   // { items, versions, modifiers, modifier_items }
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  // Key editing: "item:123" | "version:5" | "modifier:7" | "modifier_item:9" -> custom key string
  const [localKeys, setLocalKeys] = useState({});
  const [savedKeys, setSavedKeys] = useState({});
  const [saving,    setSaving]    = useState(false);

  // UI state
  const [expanded,      setExpanded]      = useState(new Set());
  const [collapsedCats, setCollapsedCats] = useState(new Set());
  const [search,        setSearch]        = useState('');

  const isDirty = JSON.stringify(localKeys) !== JSON.stringify(savedKeys);

  // ── Helpers ──────────────────────────────────────────
  function slugify(str) {
    return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
  }
  function getKey(type, id)       { return localKeys[`${type}:${id}`] || ''; }
  function setKey(type, id, val)  {
    const k = `${type}:${id}`;
    setLocalKeys(prev => {
      const n = { ...prev };
      if (val === '') delete n[k]; else n[k] = val;
      return n;
    });
  }
  function toggleExpand(id) {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleCat(cat) {
    setCollapsedCats(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });
  }

  // ── Load when both selectors have values ────────────
  useEffect(() => {
    if (!selKeyId || !selPlaceId) return;
    setData(null);
    setLoading(true);
    setLoadErr('');
    setExpanded(new Set());
    setCollapsedCats(new Set());
    setSearch('');
    apiFetch('dev/menu/items', { key_id: selKeyId, place_id: selPlaceId }, token)
      .then(d => {
        const payload = d.data || {};
        setData(payload);
        const k = payload.keys && typeof payload.keys === 'object' && !Array.isArray(payload.keys) ? payload.keys : {};
        setLocalKeys(k);
        setSavedKeys(k);
        // Collapse all categories by default
        const cats = new Set((payload.items || []).map(it => it.category));
        setCollapsedCats(cats);
      })
      .catch(err => setLoadErr(err.message || 'Failed to load menu items.'))
      .finally(() => setLoading(false));
  }, [selKeyId, selPlaceId, token]);

  // ── Save ─────────────────────────────────────────────
  async function handleSave() {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const entries = [];
      for (const [k, v] of Object.entries(localKeys)) {
        const colon = k.indexOf(':');
        entries.push({ type: k.slice(0, colon), item_id: parseInt(k.slice(colon + 1), 10), custom_key: v });
      }
      for (const k of Object.keys(savedKeys)) {
        if (!(k in localKeys)) {
          const colon = k.indexOf(':');
          entries.push({ type: k.slice(0, colon), item_id: parseInt(k.slice(colon + 1), 10), custom_key: '' });
        }
      }
      await apiFetch('dev/menu/keys/save', { key_id: selKeyId, keys: entries }, token);
      setSavedKeys({ ...localKeys });
      addToast({ title: 'Keys saved.' });
    } catch (err) {
      addToast({ title: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  }

  // ── Build lookup maps from flat arrays ───────────────
  const versionsByItem    = {};   // menu_id   → [version]
  const modifiersByItem   = {};   // menu_id   → [modifier]
  const modItemsByMod     = {};   // modifier_id → [modifier_item]
  if (data) {
    (data.versions       || []).forEach(v  => { (versionsByItem[v.menu_id]       ||= []).push(v);  });
    (data.modifiers      || []).forEach(m  => { (modifiersByItem[m.menu_id]      ||= []).push(m);  });
    (data.modifier_items || []).forEach(mi => { (modItemsByMod[mi.modifier_id]   ||= []).push(mi); });
  }

  // ── Filter & group by category ───────────────────────
  const q = search.trim().toLowerCase();
  const filteredItems = (data?.items || []).filter(it => {
    if (!q) return true;
    return (
      it.title.toLowerCase().includes(q) ||
      it.category.toLowerCase().includes(q) ||
      (getKey('item', it.id) || '').toLowerCase().includes(q)
    );
  });
  const categories = {};
  filteredItems.forEach(it => { (categories[it.category] ||= []).push(it); });

  const noSelection = !selKeyId || !selPlaceId;

  return (
    <div className="dash-content">
      <div className="section-header-row">
        <div className="section-title">Menu Items</div>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Custom keys appear in webhook payloads instead of item titles</span>
      </div>

      {/* ── Selectors + search bar (sticky, matches Merchants) ── */}
      <div className="merchants-search-bar menu-controls-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>API Key</span>
            <select className="dash-select" value={selKeyId} onChange={e => setSelKeyId(parseInt(e.target.value, 10))}>
              <option value={0}>| select |</option>
              {(keys || []).map(k => (
                <option key={k.id} value={k.id}>{k.name || k.label || `Key #${k.id}`}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Merchant</span>
            <select className="dash-select" value={selPlaceId} onChange={e => setSelPlaceId(parseInt(e.target.value, 10))}>
              <option value={0}>| select |</option>
              {linkedMerchants.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          {data && (
            <input
              className="field-input merchants-search"
              placeholder="Search items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          )}
          {data && isDirty && (
            <>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Unsaved changes</span>
              <button
                className="btn-sm accent"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── States ── */}
      {noSelection && !loading && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          Select an API key and a merchant to configure custom keys.
        </div>
      )}
      {!noSelection && loading && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>Loading…</div>
      )}
      {loadErr && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--danger)', fontSize: 13 }}>{loadErr}</div>
      )}
      {data && filteredItems.length === 0 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          {search ? 'No items match your search.' : 'No menu items found for this merchant.'}
        </div>
      )}

      {/* ── Item tree ── */}
      {data && Object.entries(categories).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catItems]) => {
        const catCollapsed = collapsedCats.has(cat);

        // Count how many keys are set across all nodes in this category
        const catTotal = catItems.reduce((sum, item) => {
          const vArr = versionsByItem[item.id]  || [];
          const mArr = modifiersByItem[item.id] || [];
          const visibleMods = mArr.filter(m => (modItemsByMod[m.id] || []).length > 0);
          const mItems = visibleMods.flatMap(m => modItemsByMod[m.id] || []);
          return sum + 1 + vArr.length + visibleMods.length + mItems.length;
        }, 0);
        const catFilled = catItems.reduce((sum, item) => {
          const vArr   = versionsByItem[item.id]  || [];
          const mArr   = modifiersByItem[item.id] || [];
          const visibleMods = mArr.filter(m => (modItemsByMod[m.id] || []).length > 0);
          const mItems = visibleMods.flatMap(m => modItemsByMod[m.id] || []);
          const itemSet   = getKey('item', item.id)             ? 1 : 0;
          const verSet    = vArr.filter(v  => getKey('version', v.id)).length;
          const modGroupSet = visibleMods.filter(m => getKey('modifier', m.id)).length;
          const modSet    = mItems.filter(mi => getKey('modifier_item', mi.id)).length;
          return sum + itemSet + verSet + modGroupSet + modSet;
        }, 0);
        const catStatus = catFilled === 0 ? 'none' : catFilled === catTotal ? 'full' : 'partial';

        return (
          <div key={cat} className="menu-cat-block">
            {/* Category header */}
            <div className="menu-cat-header" onClick={() => toggleCat(cat)}>
              <ChevronRight size={13} className={`menu-cat-chevron${catCollapsed ? '' : ' open'}`} />
              <span className="menu-cat-name">{cat}</span>
              <span className={`menu-cat-count menu-cat-count--${catStatus}`}>
                {catFilled}/{catTotal}
              </span>
            </div>

            {!catCollapsed && (
              <div className="menu-items-list">
                {catItems.map(item => {
                  const isExpanded  = expanded.has(item.id);
                  const versions    = versionsByItem[item.id]  || [];
                  const modifiers   = modifiersByItem[item.id] || [];
                  const visibleModifiers = modifiers.filter(m => (modItemsByMod[m.id] || []).length > 0);
                  const allModItems = visibleModifiers.flatMap(m => modItemsByMod[m.id] || []);
                  const totalMods   = allModItems.length;
                  const hasChildren = versions.length > 0 || totalMods > 0;

                  const itemKeyFilled  = !!getKey('item', item.id);
                  const childTotal     = versions.length + visibleModifiers.length + totalMods;
                  const childFilled    = versions.filter(v => getKey('version', v.id)).length
                                       + visibleModifiers.filter(m => getKey('modifier', m.id)).length
                                       + allModItems.filter(mi => getKey('modifier_item', mi.id)).length;
                  const childStatus    = childTotal === 0 ? null
                                      : childFilled === childTotal ? 'full'
                                      : childFilled > 0 ? 'partial' : 'none';

                  return (
                    <div key={item.id} className={`menu-item-block${isExpanded ? ' expanded' : ''}${!item.active ? ' inactive' : ''}`}>

                      {/* ── Item row ── */}
                      <div
                        className="menu-item-row"
                        onClick={() => hasChildren && toggleExpand(item.id)}
                        style={hasChildren ? { cursor: 'pointer' } : undefined}
                      >
                        <ChevronRight size={12} className={`menu-row-chevron${isExpanded ? ' open' : ''}${!hasChildren ? ' hidden' : ''}`} />

                        <span className="menu-item-title">{item.title}</span>

                        {hasChildren && childStatus && (
                          <span className={`menu-child-hint menu-child-hint--${childStatus}`}>
                            {childFilled}/{childTotal}
                          </span>
                        )}

                        <input
                          className={`field-input menu-key-input${itemKeyFilled ? ' menu-key-input--filled' : ''}`}
                          placeholder={slugify(item.title)}
                          value={getKey('item', item.id)}
                          onChange={e => setKey('item', item.id, e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                          onClick={e => e.stopPropagation()}
                          spellCheck={false}
                        />
                      </div>

                      {/* ── Expanded children ── */}
                      {isExpanded && (
                        <div className="menu-item-children">

                          {/* Variants */}
                          {versions.length > 0 && (
                            <div className="menu-child-group">
                              <div className="menu-child-group-label">Variants</div>
                              {versions.map(v => {
                                const vFilled = !!getKey('version', v.id);
                                return (
                                  <div key={v.id} className={`menu-child-row${!v.active ? ' inactive' : ''}`}>
                                    <span className={`menu-child-dot${vFilled ? ' filled' : ''}`} />
                                    <span className="menu-child-title">{v.title}</span>
                                    <input
                                      className={`field-input menu-key-input${vFilled ? ' menu-key-input--filled' : ''}`}
                                      placeholder={slugify(v.title)}
                                      value={getKey('version', v.id)}
                                      onChange={e => setKey('version', v.id, e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                      spellCheck={false}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Modifier groups */}
                          {modifiers.map(mod => {
                            const modItems = modItemsByMod[mod.id] || [];
                            if (modItems.length === 0) return null;
                            const modFilled = !!getKey('modifier', mod.id);
                            return (
                              <div key={mod.id} className="menu-child-group">
                                <div className="menu-child-group-label-row">
                                  <div className="menu-child-group-label">{mod.title}</div>
                                  <input
                                    className={`field-input menu-key-input${modFilled ? ' menu-key-input--filled' : ''}`}
                                    placeholder={slugify(mod.title)}
                                    value={getKey('modifier', mod.id)}
                                    onChange={e => setKey('modifier', mod.id, e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                    spellCheck={false}
                                  />
                                </div>
                                {modItems.map(mi => {
                                  const miFilled = !!getKey('modifier_item', mi.id);
                                  return (
                                    <div key={mi.id} className={`menu-child-row${!mi.active ? ' inactive' : ''}`}>
                                      <span className={`menu-child-dot${miFilled ? ' filled' : ''}`} />
                                      <span className="menu-child-title">{mi.title}</span>
                                      <input
                                        className={`field-input menu-key-input${miFilled ? ' menu-key-input--filled' : ''}`}
                                        placeholder={slugify(mi.title)}
                                        value={getKey('modifier_item', mi.id)}
                                        onChange={e => setKey('modifier_item', mi.id, e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                        spellCheck={false}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────
export default function Home() {
  const [page, setPage] = useState(() => {
    const hash = window.location.hash.slice(1);
    const valid = ['overview', 'api-keys', 'webhooks', 'logs', 'merchants', 'menu-items', 'endpoints'];
    return valid.includes(hash) ? hash : 'overview';
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const { token, account, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { openDrawer } = useDrawer();
  const { addToast }   = useToast();
  const [signingOut, setSigningOut] = useState(false);

  // ── Shared state loaded from API ─────────────────────────────
  const [keys,           setKeys]           = useState(null);
  const [merchants,      setMerchants]      = useState(null);
  const [webhookConfigs, setWebhookConfigs] = useState({});
  const [logs,           setLogs]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(null);
  const [webhooksKeyId,  setWebhooksKeyId]  = useState(null);
  const [logsKeyId,      setLogsKeyId]      = useState(0);
  const [live,           setLive]           = useState(true);
  const [logsReady,     setLogsReady]     = useState(false);
  const [recentLogs,    setRecentLogs]    = useState([]);
  const [stats,         setStats]         = useState(null);
  const lastLogsIdRef = useRef(0);

  function loadDashboard() {
    setLoading(true);
    setLoadError(null);
    Promise.all([
      apiFetch('dev/merchants',    {}, token),
      apiFetch('dev/keys',         {}, token),
      apiFetch('dev/webhooks',     {}, token),
      apiFetch('dev/stats',        {}, token),
      apiFetch('dev/logs/overview',{}, token),
    ]).then(([mData, kData, wData, sData, ovData]) => {
      setMerchants(mData.data.merchants);
      setKeys(kData.data.keys);
      setWebhookConfigs(wData.data.webhooks || {});
      setStats(sData.data);
      const ovLogs = ovData.data.logs || [];
      setRecentLogs(ovLogs);
      if (ovLogs.length > 0) {
        lastLogsIdRef.current = Math.max(...ovLogs.map(l => l.id));
      }
      setLoading(false);
    }).catch(err => {
      setLoadError(err.message);
      setLoading(false);
    });
  }

  useEffect(() => { loadDashboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── initLogs: called by LogsPage on first visit ───────────────
  function initLogs(fetchedLogs) {
    setLogs(fetchedLogs);
    if (fetchedLogs.length > 0) {
      lastLogsIdRef.current = Math.max(lastLogsIdRef.current, ...fetchedLogs.map(l => l.id));
    }
    setLogsReady(true);
  }

  // ── Poll for new logs | only after initial fetch completes ───
  useEffect(() => {
    if (!logsReady || !live) return;
    const interval = setInterval(async () => {
      try {
        const data = await apiFetch('dev/logs/recent', { since_id: lastLogsIdRef.current }, token);
        const incoming = data.data?.logs ?? [];
        if (incoming.length > 0) {
          setLogs(prev => [...[...incoming].reverse(), ...prev]);
          lastLogsIdRef.current = incoming[incoming.length - 1].id;
          const latest = incoming[incoming.length - 1];
          const isNew  = latest.event === 'order.new';
          addToast({
            title: isNew ? 'New order received' : 'Order updated',
            body: `#${latest.order_id} · ${latest.merchant_token}${
              latest.order_status ? ' → ' + latest.order_status : ''
            }`,
            cta: {
              label: 'View log →',
              onClick: () => openDrawer(
                <LogOrderDetail log={latest} token={token} onRetried={(id, status, http) =>
                  setLogs(prev => prev.map(l => l.id === id ? { ...l, status, http_status: http } : l))
                } />
              ),
            },
          });
        }
      } catch { /* ignore poll errors silently */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [logsReady, live, token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSignOut() {
    setSigningOut(true);
    try {
      await fetch(`${API_BASE}/auth/sign_out`, {
        method: 'POST',
        headers: { AUTH: token },
      });
    } catch { /* ignore network errors | still log out locally */ }
    logout();
    navigate('/login');
  }

  function handleSignOut() {
    openModal({
      title: 'Sign out',
      text: 'Are you sure you want to sign out?',
      confirm: { label: 'Sign out', danger: true, onClick: doSignOut },
      cancel: { label: 'Stay signed in' },
    });
  }

  function navTo(p) {
    setPage(p);
    setMenuOpen(false);
    window.location.hash = p;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function goToWebhook(keyId) {
    setWebhooksKeyId(keyId);
    navTo('webhooks');
  }

  // Sync key selection between tabs.
  // Logs → specific key: update webhooks too. Logs → All keys (0): leave webhooks alone.
  function handleLogsKeyChange(kid) {
    setLogsKeyId(kid);
    if (kid !== 0) setWebhooksKeyId(kid);
  }
  // Webhooks key change: always sync logs to the same specific key.
  function handleWebhooksKeyChange(kid) {
    setWebhooksKeyId(kid);
    setLogsKeyId(kid);
  }

  const PAGE_TITLES = {
    overview:      'Overview',
    'api-keys':    'API Keys',
    webhooks:      'Webhooks',
    logs:          'Event Logs',
    merchants:     'Merchants',
    'menu-items':  'Menu Items',
    endpoints:     'Endpoints',
  };

  return (
    <div className="dash-layout">
      {/* ── Mobile nav overlay ── */}
      {menuOpen && <div className="mobile-overlay" onClick={() => setMenuOpen(false)} />}

      <aside className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <Link
          to="/"
          className="sidebar-brand"
          style={{ textDecoration: 'none' }}
          onClick={e => { if (page !== 'overview') { e.preventDefault(); navTo('overview'); } }}
        >
          <img src={logo} alt="" />
          <div>
            <div className="sidebar-brand-text">Ride Eats</div>
            <div className="sidebar-brand-sub">Developers</div>
          </div>
          <button className="sidebar-close" onClick={e => { e.preventDefault(); setMenuOpen(false); }}>
            <X size={18} />
          </button>
        </Link>

        <nav className="sidebar-nav">
          <div>
            <div className="sidebar-section-label">Dashboard</div>
            <a
              href="#overview"
              className={`sidebar-item${page === 'overview' ? ' active' : ''}`}
              style={{ textDecoration: 'none' }}
              onClick={e => { e.preventDefault(); navTo('overview'); }}
            >
              <LayoutDashboard size={14} className="sicon-svg" />Overview
            </a>
          </div>

          <div>
            <div className="sidebar-section-label">Keys &amp; Events</div>
            <a href="#api-keys" className={`sidebar-item${page === 'api-keys' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('api-keys'); }}>
              <Key size={14} className="sicon-svg" />API Keys
            </a>
            <a href="#webhooks" className={`sidebar-item${page === 'webhooks' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('webhooks'); }}>
              <Webhook size={14} className="sicon-svg" />Webhooks
            </a>
            <a href="#logs" className={`sidebar-item${page === 'logs' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('logs'); }}>
              <ScrollText size={14} className="sicon-svg" />Event Logs
            </a>
          </div>

          <div>
            <div className="sidebar-section-label">Config</div>
            <a href="#merchants" className={`sidebar-item${page === 'merchants' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('merchants'); }}>
              <Store size={14} className="sicon-svg" />Merchants
            </a>
            <a href="#menu-items" className={`sidebar-item${page === 'menu-items' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('menu-items'); }}>
              <UtensilsCrossed size={14} className="sicon-svg" />Menu Items
            </a>
            <a href="#endpoints" className={`sidebar-item${page === 'endpoints' ? ' active' : ''}`} style={{ textDecoration: 'none' }} onClick={e => { e.preventDefault(); navTo('endpoints'); }}>
              <Plug size={14} className="sicon-svg" />Endpoints
            </a>
          </div>
        </nav>

        <div className="sidebar-footer">
          <Link to="/docs" className="sidebar-item" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
            <BookOpen size={14} className="sicon-svg" />API Reference
          </Link>
        </div>
      </aside>

      <div className="dash-main">
        <div className="dash-topbar">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
              <Menu size={20} />
            </button>
            <span className="dash-page-title">{PAGE_TITLES[page]}</span>
          </div>
          <div className="topbar-actions">
            <button className="btn-sm">v1.0</button>
            <UserMenu name={account?.name} onSignOut={handleSignOut} signingOut={signingOut} />
          </div>
        </div>

        {page === 'endpoints' && <EndpointsPage />}
        {(page === 'overview' || page === 'api-keys' || page === 'webhooks' || page === 'merchants' || page === 'menu-items' || page === 'logs') && (
          loading
            ? <LoadingState rows={4} />
            : loadError
              ? <ErrorState message={loadError} onRetry={loadDashboard} />
              : <>
                  {page === 'overview'  && <OverviewPage setPage={setPage} keys={keys} merchants={merchants} recentLogs={recentLogs} stats={stats} token={token} />}
                  {page === 'api-keys'  && <ApiKeysPage keys={keys} setKeys={setKeys} merchants={merchants} token={token} onSetupWebhook={goToWebhook} />}
                  {page === 'webhooks'  && <WebhooksPage keys={keys} webhookConfigs={webhookConfigs} setWebhookConfigs={setWebhookConfigs} token={token} selectedKeyId={webhooksKeyId} setSelectedKeyId={handleWebhooksKeyChange} />}
                  {page === 'merchants'  && <MerchantsPage merchants={merchants} setMerchants={setMerchants} token={token} isAdmin={isAdmin} />}
                  {page === 'menu-items' && <MenuItemsPage merchants={merchants} token={token} keys={keys || []} />}
                  {page === 'logs'       && <LogsPage logs={logs} setLogs={setLogs} live={live} setLive={setLive} token={token} keys={keys || []} keyFilter={logsKeyId} setKeyFilter={handleLogsKeyChange} logsReady={logsReady} initLogs={initLogs} />}
                </>
        )}
      </div>
    </div>
  );
}
